import { Element, VideoEditor } from '../editor';
import { parseKaraokeDataFromLegacyTranscriptElements } from '../editor/services/legacyKaraokeImportExport';
import { KARAOKE_TRACK_NUMBER } from '../stores/VideoCreatorStore';
import { KaraokeStore } from '../stores-v2';
import { LogManager } from '../utility/Logger';
import {
  getClosestNotRemovedTextIndexToLeft,
  getClosestNotRemovedTextIndexToRight,
} from '../videoTranscriptionProcessor/utils';
import { saveVideoResources } from './VideoResourcesProvider';

import type { KaraokeData } from '../editor/types/karaoke';
import type { IVideoCreatorStore } from '../stores/IVideoCreatorStore';
import type { VideoSource } from '../stores-v2';
import type { SourcePlatform, Video, VideoAction } from '../types.ts/story';
import type { TranscriptElement } from '../types.ts/transcript';
import type { ClipFragment } from './AIClipProducer';
import type { VideoResourceField } from './VideoResourcesProvider';

const logger = LogManager.getLogger('AIClipProcessor');

export class AIClipProcessor {
  private clip: ClipFragment;
  private videoCreator: IVideoCreatorStore;
  private originalVideo: Video;
  private sourcePlatform: SourcePlatform;

  constructor(
    clip: ClipFragment,
    videoCreator: IVideoCreatorStore,
    originalVideo: Video,
    sourcePlatform: SourcePlatform,
  ) {
    this.clip = clip;
    this.videoCreator = videoCreator;
    this.originalVideo = originalVideo;
    this.sourcePlatform = sourcePlatform;
    logger.debug('initialized', {
      clip,
      sourcePlatform,
    });
  }

  // main method
  public async save(): Promise<Video> {
    logger.debug('save:start');
    const video = this.newVideoFromClip();
    const croppedVideo = this.cropVideo(video);
    const saved = await this.saveVideo(croppedVideo);
    logger.debug('save:completed', { videoId: saved.id });
    return saved;
  }

  private newVideoFromClip(): Video {
    logger.debug('newVideoFromClip:start');
    const video = this.videoCreator.prepareAsCurrentLoadedVideo(
      this.originalVideo,
      this.clip,
      this.sourcePlatform,
    );
    video.lastActionJson = this.videoAction;
    if (this.isByExternalUser) {
      video.isClientReady = true;
    }
    logger.debug('newVideoFromClip:prepared', {
      video,
    });
    return video;
  }

  private get clipJson(): Video['clipJson'] {
    const { clip } = this;
    return {
      startTime: clip.startTime,
      duration: clip.duration,
      muxPlaybackId: clip.muxPlaybackId,
      impactScore: clip.impactScore,
      isReel: clip.isReel,
    };
  }

  private get videoAction(): VideoAction {
    return {
      editor: this.videoCreator.rootStore.userIdentityStore.currentEditor,
      date: new Date().getTime(),
      type: 'save',
    };
  }

  private get isByExternalUser(): boolean {
    return !!this.videoCreator.story?.byExternalUser;
  }

  private cropVideo(video: Video): Video {
    logger.debug('crop:start');
    const editor = AIClipProcessor.newVideoEditor(video, this.videoCreator);

    this.ensureClipData(editor);

    this.cropTimelineToClip(editor);
    logger.debug('crop:timeline-crop-done');

    this.serializeCroppedVideo(video, editor);
    logger.debug('crop:serialize-done');

    return video;
  }

  private serializeCroppedVideo(video: Video, editor: VideoEditor) {
    try {
      video.videoSource = editor.timeline.toJSON();
      video.transcriptionSnapshot = {
        elements: editor.getFullTranscript() as TranscriptElement[],
      };
      video.karaokeData = this.serializeKaraokeData(editor);
      video.clipJson = this.clipJson; // some properties might still be missing in newVideoFromClip
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      logger.error('crop:serialize-failed', { error: err.message });
      throw new Error('Failed to serialize cropped video');
    }
  }

  private serializeKaraokeData(editor: VideoEditor): KaraokeData | undefined {
    const store = new KaraokeStore();
    store.syncFromEditor(editor);
    return store.getKaraokeData() ?? undefined;
  }

  private cropTimelineToClip(editor: VideoEditor) {
    try {
      const keepStart = this.clip.startTime;
      const keepEnd = this.clip.startTime + this.clip.duration;
      editor.commandCropTimeline(keepStart, keepEnd);
      logger.debug('crop:timeline-crop-done', { keepStart, keepEnd });
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      logger.warn('crop:timeline-crop-failed', { error: err.message });
    }
  }

  // Ensure clip has startTime and duration set, try to use transcript if missing.
  // Happens in case of saving clip from transcript
  private ensureClipData(editor: VideoEditor) {
    if (typeof this.clip.startTime !== 'number') {
      const full =
        this.clip.transcriptPosition.relevantElements ||
        (editor.getFullTranscript() as TranscriptElement[]);
      const elementIndex = getClosestNotRemovedTextIndexToRight(
        this.clip.transcriptPosition.startIndex,
        full,
      );
      if (elementIndex === -1) {
        throw new Error('Cannot determine clip start time: index not found');
      }
      const element = full[elementIndex];
      if (element.ts == null) {
        throw new Error('Cannot determine clip start time: ts is null');
      }
      this.clip.startTime = element.ts;
    }
    if (typeof this.clip.duration !== 'number') {
      const full =
        this.clip.transcriptPosition.relevantElements ||
        (editor.getFullTranscript() as TranscriptElement[]);
      const elementIndex = getClosestNotRemovedTextIndexToLeft(
        this.clip.transcriptPosition.endIndex,
        full,
      );
      if (elementIndex === -1) {
        throw new Error('Cannot determine clip duration: index not found');
      }
      const element = full[elementIndex];
      if (element.end_ts == null) {
        throw new Error('Cannot determine clip duration: end_ts is null');
      }
      this.clip.duration = element.end_ts - this.clip.startTime;
      if (this.clip.duration < 0) {
        throw new Error('Invalid clip duration');
      }
    }
  }

  public static newVideoEditor(
    video: Video,
    videoCreator: IVideoCreatorStore,
  ): VideoEditor {
    const editor = new VideoEditor();

    // Enable new transcript architecture for cropping
    editor.setFlag('enableTranscriptSegmentArchitecture', true);
    editor.setFlag('enableTranscriptMigrationValidation', false);

    editor.loadProject(video.videoSource);
    AIClipProcessor.hydrateOriginalMediaDuration(editor, videoCreator);
    const ok = editor.loadTranscript(
      video.transcriptionSnapshot?.elements ??
        videoCreator.rootStore.transcriptionStore.getFinalTranscriptionElements(),
      videoCreator.rootStore.transcriptionStore.getOriginalTranscription()
        ?.elements || [],
    );
    if (!ok) {
      logger.error('editor:load-transcript-failed');
      throw new Error('Failed to load transcript into temp video editor');
    }

    AIClipProcessor.loadKaraokeData(video, videoCreator, editor);

    return editor;
  }

  private static loadKaraokeData(
    video: Video,
    videoCreator: IVideoCreatorStore,
    editor: VideoEditor,
  ): void {
    const karaokeStore = videoCreator.rootStore.karaokeStore;
    let karaokeData =
      karaokeStore.getKaraokeData() ?? video.karaokeData ?? null;
    const hasKaraokeTrackInSource = (videoSource: VideoSource) => {
      return !!videoSource.elements?.find(
        el => el.track === KARAOKE_TRACK_NUMBER,
      );
    };
    if (
      !karaokeData &&
      hasKaraokeTrackInSource(video.videoSource) &&
      karaokeStore.legacyKaraokeConfigLoaded
    ) {
      const migratedConfig =
        videoCreator.rootStore.editorStore.editor.createKaraokeConfig(
          karaokeStore.legacyKaraokeConfigLoaded,
        );
      karaokeData = parseKaraokeDataFromLegacyTranscriptElements(
        editor.getFullTranscript(),
        migratedConfig,
      );
    }
    if (karaokeData) {
      editor.loadKaraokeData(karaokeData);
    }
  }

  private static hydrateOriginalMediaDuration(
    editor: VideoEditor,
    videoCreator: IVideoCreatorStore,
  ): void {
    try {
      const story = videoCreator.story;
      const fullMediaDuration: number | undefined =
        story?.originalVideo?.video?.duration;
      if (typeof fullMediaDuration !== 'number' || fullMediaDuration <= 0) {
        logger.debug('hydrateMediaDuration:skip:no-duration');
        return;
      }
      for (const track of editor.timeline.tracks) {
        for (const el of track.elements) {
          if (
            !Element.isVideoElement(el) ||
            !videoCreator.isOriginalVideoElement(el.getState()) ||
            el.media_duration != null
          ) {
            continue;
          }
          el.media_duration = fullMediaDuration;
          logger.debug('hydrateMediaDuration:set', {
            elementId: el.id,
            fullMediaDuration,
          });
        }
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      logger.warn('hydrateMediaDuration:error', { error: err.message });
    }
  }

  private async saveVideo(video: Video): Promise<Video> {
    logger.debug('saveVideo:start');
    const store = this.videoCreator;
    const story = store.story;
    const repos = store.datoClientStore;
    if (!story || !repos?.videoRepository) {
      logger.error('saveVideo:missing-deps');
      throw new Error('Cannot save clip: missing story or video repository');
    }

    if (!video.transcriptionSnapshot?.elements) {
      logger.error('saveVideo:no-transcription-snapshot');
      throw new Error(
        'No transcription snapshot present on video, cannot save',
      );
    }

    // Persist videoSource snapshot resource
    const resourcesMetadata = await saveVideoResources(
      story.id,
      {
        videoSourceSnapshot: video.videoSource,
        ...(store.flags.enableTranscriptSnapshotResourceUpload && {
          transcriptionSnapshot: video.transcriptionSnapshot,
        }),
        ...(video.karaokeData && {
          karaokeData: video.karaokeData,
        }),
      },
      {
        useS3: store.flags.enableVideoResourcesS3Upload ?? true,
        dualWrite: store.flags.enableVideoResourcesS3DualWrite ?? false,
      },
    );

    if (
      !resourcesMetadata ||
      Object.keys(resourcesMetadata).some(
        key => !resourcesMetadata[key as VideoResourceField],
      )
    ) {
      logger.error('saveVideo:invalid-resources-metadata', {
        resourcesMetadata,
      });
      throw new Error('Failed to save video resources metadata');
    }
    if (
      store.flags.enableTranscriptSnapshotResourceUpload &&
      !resourcesMetadata.transcriptionSnapshot
    ) {
      throw new Error('Failed to save transcription snapshot metadata');
    }
    if (!resourcesMetadata.videoSourceSnapshot) {
      throw new Error('Failed to save video source snapshot metadata');
    }
    if (video.karaokeData && !resourcesMetadata.karaokeData) {
      logger.warn('Did not save karaoke snapshot metadata');
    }

    const migrationLog =
      store.rootStore.editorStore.transcriptSegmentArchitectureMigrationLog;

    // Build minimal payload for repository
    const payload = {
      ...video,
      resourcesMetadata: {
        videoSourceSnapshot: resourcesMetadata.videoSourceSnapshot,
        ...(resourcesMetadata.transcriptionSnapshot && {
          transcriptionSnapshot: resourcesMetadata.transcriptionSnapshot,
        }),
        ...(resourcesMetadata.karaokeData && {
          karaokeData: resourcesMetadata.karaokeData,
        }),
      },
      ...((!resourcesMetadata.transcriptionSnapshot ||
        store.flags.enableTranscriptSnapshotResourceDualWrite) && {
        transcriptionSnapshot: {
          elements: video.transcriptionSnapshot?.elements || [],
        },
      }),
      ...(resourcesMetadata.transcriptionSnapshot &&
        !store.flags.enableTranscriptSnapshotResourceDualWrite && {
          // TODO: implement before enableTranscriptSnapshotResourceDualWrite should be disabled
          // for migration enableTranscriptSnapshotResourceDualWrite should be enabled, so this block will not be executed
          transcriptionChanges: [],
        }),
      transcriptionMigrationStatus: migrationLog?.status,
      transcriptionMigrationLog: migrationLog?.debugInfo,
      subtitles: undefined, // not generating here
    };

    const { id, slug, hash } =
      await repos.videoRepository.saveOrUpdateVideo(payload);

    video.id = id;
    video.slug = slug;
    video.hash = hash;
    video._publishedAt = new Date().toISOString();
    logger.debug('saveVideo:success', { id, slug });

    return video;
  }
}
